import React, {useEffect, useState} from "react";
import {FormControlLabel, MenuItem, Radio, RadioGroup, Stack, TextField} from "@mui/material";
import {toast} from "react-toastify";
import {SubProjectRepository} from "../../../../repositories/ProjectRepository";
import {varsListToObject, varsObjectToList} from "./VariablesEditor";
import {subProjectLabel} from "../../../../utils/subProjects";

// Extension variables set from the sub project / version selection instead of the variable list
const PROJECT_VAR = "PROJECT_ID";
const SUB_PROJECT_VAR = "SUB_PROJECT_ID";
const FLOW_UID_VAR = "FLOW_UID";
const VERSION_TYPE_VAR = "PROJECT_VERSION_TYPE";
const VERSION_VAR = "PROJECT_VERSION";
// Set by extensions saved before the rename; read as a fallback and dropped on the next save
const LEGACY_SUB_PROJECT_VAR = "LANGUAGE_PROJECT_ID";
const MANAGED_VARS = [PROJECT_VAR, SUB_PROJECT_VAR, LEGACY_SUB_PROJECT_VAR, FLOW_UID_VAR, VERSION_TYPE_VAR, VERSION_VAR];

export const LATEST_VERSION = "latest";

// Values of PROJECT_VERSION_TYPE, by the version type of the Project API
const VERSION_TYPES = [
  {value: "PRODUCTION", label: "Production", type: "PRODUCTION"},
  {value: "STAGE", label: "Stage", type: "STAGE"},
];
export const DEFAULT_VERSION_TYPE = VERSION_TYPES[0].value;

// Selection stored in an extension's variables: {subProjectId, versionType, version}. The owner
// (sub_project_id) wins; older extensions fall back to SUB_PROJECT_ID (or the legacy LANGUAGE_PROJECT_ID / language_project_id),
// then to their flow uid.
export const getTarget = (extension, subProjects) => {
  const variables = extension?.variables || {};
  const flowUid = variables[FLOW_UID_VAR] || extension?.flow_uid;
  const ownerId = extension?.sub_project_id || extension?.language_project_id
    || variables[SUB_PROJECT_VAR] || variables[LEGACY_SUB_PROJECT_VAR];
  const subProject = subProjects.find(item => String(item.id) === String(ownerId))
    || subProjects.find(item => item.flowUid === flowUid);
  return {
    subProjectId: subProject?.id ?? null,
    versionType: VERSION_TYPES.find(item => item.value === String(variables[VERSION_TYPE_VAR] || "").toLowerCase())?.value
      || DEFAULT_VERSION_TYPE,
    version: String(variables[VERSION_VAR] || LATEST_VERSION),
  };
};

// Rows of the variable list, without the variables set from the selection
export const toVariableRows = (variables) =>
  varsObjectToList(variables).filter(row => !MANAGED_VARS.includes(row.key));

// Variables as saved: the variable list plus the selection (and PROJECT_ID when projectId is given). Without a
// selected sub project the extension keeps the managed variables it already has.
export const toVariables = (rows, target, subProjects, currentVariables = {}, projectId) => {
  const subProject = subProjects.find(item => item.id === target.subProjectId);
  const managed = subProject
    ? {
      ...(projectId != null ? {[PROJECT_VAR]: String(projectId)} : {}),
      [SUB_PROJECT_VAR]: String(subProject.id),
      [FLOW_UID_VAR]: subProject.flowUid,
      [VERSION_TYPE_VAR]: target.versionType || DEFAULT_VERSION_TYPE,
      [VERSION_VAR]: target.version || LATEST_VERSION,
    }
    : Object.fromEntries(MANAGED_VARS.filter(key => key in currentVariables && key !== LEGACY_SUB_PROJECT_VAR)
      .map(key => [key, currentVariables[key]]));
  return {...varsListToObject(rows.filter(row => !MANAGED_VARS.includes(row.key.trim()))), ...managed};
};

// versions created before numbering have no name
const versionLabel = (version) => version.name || version.versionUid;

// Sub project, version type and version an extension runs; shown above the variable list
export const SubProjectFields = ({subProjects, target, onChange, required}) => {
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    setVersions([]);
    if (!target.subProjectId) return;
    let cancelled = false;
    SubProjectRepository.getVersions(target.subProjectId)
      .then(response => !cancelled && setVersions(response.data || []))
      .catch(e => {
        console.error(e);
        if (!cancelled) toast.error(e?.response?.data?.message || "Failed to load versions");
      });
    return () => {
      cancelled = true;
    };
  }, [target.subProjectId]);

  // only the versions of the selected type can be picked
  const versionType = VERSION_TYPES.find(item => item.value === target.versionType)?.type;
  const typeVersions = versions.filter(version => version.type === versionType);
  // the saved version stays selected while the versions load, or when it is not a version of this type
  const savedVersion = versions.find(version => String(version.id) === target.version);
  const isKnownVersion = target.version === LATEST_VERSION || typeVersions.includes(savedVersion);

  return (
    <Stack gap={1}>
      <RadioGroup
        row
        value={target.versionType}
        onChange={e => onChange({...target, versionType: e.target.value, version: LATEST_VERSION})}
      >
        {VERSION_TYPES.map(item => (
          <FormControlLabel
            key={item.value} value={item.value} label={item.label}
            control={<Radio size={"small"}/>}
            componentsProps={{typography: {fontSize: 14}}}
          />
        ))}
      </RadioGroup>
      <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
        <TextField
          select size={"small"} label={"Sub project"} required={required}
          value={target.subProjectId ?? ""}
          onChange={e => onChange({...target, subProjectId: e.target.value, version: LATEST_VERSION})}
          sx={{flex: 1}}
        >
          {subProjects.map(item => (
            <MenuItem key={item.id} value={item.id}>{subProjectLabel(item)}</MenuItem>
          ))}
        </TextField>
        <TextField
          select size={"small"} label={"Version"}
          value={target.version}
          disabled={!target.subProjectId}
          onChange={e => onChange({...target, version: e.target.value})}
          sx={{flex: 1}}
        >
          <MenuItem value={LATEST_VERSION}>Latest</MenuItem>
          {typeVersions.map(version => (
            <MenuItem key={version.id} value={String(version.id)}>{versionLabel(version)}</MenuItem>
          ))}
          {!isKnownVersion && (
            <MenuItem value={target.version}>
              {savedVersion ? versionLabel(savedVersion) : `Version ${target.version}`}
            </MenuItem>
          )}
        </TextField>
      </Stack>
    </Stack>
  );
};
